import { FC, useEffect, useMemo } from 'react';
import { Box } from '@rebass/emotion';
import { useQuery } from '@apollo/client';
import { useFormContext, Controller } from 'react-hook-form';

import { isNotNullish } from '@app/helpers/predicate';
import HookFormErrors from '@app/components/HookFormErrors';
import { getLocalizationCountryCookie } from '@app/helpers/localization';

import AddressCard from './AddressCard';
import AddAddressButton from './AddAddressButton';
import { CustomerAndCartAddressesQuery } from './__generated__/CustomerAndCartAddressesQuery';
import { isAddressEqual, mapToAddressType } from '../helpers';
import { AddressFormFields } from '../Address.constants';
import { AddressType } from '../Address.types';
import ValidationMessage from '../ValidationMessage/ValidationMessage';
import { CUSTOMER_AND_CART_ADDRESSES } from './CustomerAddresses.queries';

const CustomerAddresses: FC<{ isVirtualCart?: boolean }> = () => {
  const { data, loading } = useQuery<CustomerAndCartAddressesQuery>(
    CUSTOMER_AND_CART_ADDRESSES,
    {
      fetchPolicy: 'network-only',
      ssr: false,
      onCompleted: () => {
        setValue(AddressFormFields.FormVisible, !addresses?.length);
      },
    }
  );
  const cartAddress = useMemo(() => getCartAddress(data), [data]);
  const customerAddresses = useMemo(() => getCustomerAddresses(data), [data]);
  const addresses = useMemo(
    () => getRelevantAddresses(customerAddresses, cartAddress),
    [cartAddress, customerAddresses]
  );
  const { setValue, getValues, errors } = useFormContext();
  const isAddressFormVisible = getValues(AddressFormFields.FormVisible);

  useEffect(() => {
    if (cartAddress) setValue(AddressFormFields.Selected, cartAddress);
  }, [setValue, cartAddress]);

  return loading || !addresses ? null : (
    <>
      <HookFormErrors fields={[AddressFormFields.Selected]} />

      <Controller
        name={AddressFormFields.Selected}
        defaultValue=""
        rules={{
          required: {
            value: !isAddressFormVisible,
            //  @ts-expect-error: TS only support string, not ReactElement (react-hook-form.com/api#register)
            message: (
              <ValidationMessage type="required" fieldNameKey="selected" />
            ),
          },
        }}
        render={({ onChange, value }) => (
          <>
            {addresses.map((address, idx) => (
              <AddressCard
                idx={idx}
                key={address?.id || 'cart-address'}
                hasError={!!errors[AddressFormFields.Selected]}
                address={address}
                selected={!isAddressFormVisible && address?.id === value?.id}
                onSelect={() => {
                  onChange(address);
                  setValue(AddressFormFields.FormVisible, false);
                }}
              />
            ))}
          </>
        )}
      />

      <Box width={[1, 1 / 2]} pr={[0, 10]} mb={20}>
        <AddAddressButton />
      </Box>
    </>
  );
};

export default CustomerAddresses;

const getCustomerAddresses = (data?: CustomerAndCartAddressesQuery) =>
  data?.magento2?.customer?.addresses
    ?.map(mapToAddressType)
    .filter(isNotNullish);

const getCartAddress = (
  data?: CustomerAndCartAddressesQuery
): AddressType | null => {
  const cartAddress = mapToAddressType(
    data?.magento2?.customerCart?.billingAddress ?? null
  );

  if (!cartAddress) return null;

  const customerAddressFromCart = getCustomerAddresses(data)?.find((address) =>
    isAddressEqual(address, cartAddress)
  );
  return customerAddressFromCart || cartAddress;
};

/*
 * This assumes Magento 2 and our geolocation service
 * consistently use the same country spec (ISO-3166),
 * i.e. the cookie value is necessarily a valid address country value.
 */
const isEligibleForStore = (address: AddressType | null) =>
  address?.country_code === getLocalizationCountryCookie();

const getRelevantAddresses = (
  customerAddresses?: AddressType[],
  cartAddress?: AddressType | null
): AddressType[] | null => {
  const addresses = customerAddresses?.length ? [...customerAddresses] : [];

  if (cartAddress && cartAddress?.__typename !== 'Magento2_CustomerAddress') {
    addresses.push(cartAddress);
  }

  return addresses.length ? addresses.filter(isEligibleForStore) : null;
};
